import React, { useState, useEffect, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Div, Icon, Input, Label, Row, Switch, Text } from '@pinpoint/design-kit';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import CONSTANTS from 'src/store/constants';
import { usersActions, appActions } from 'src/store/actions';
import { Page, Rbac } from 'src/containers';
import { AgencyDropdown, ClientsSelection, RolesDropdown, SiteRolesDropdown, Button } from 'src/components';
import { uuid } from 'src/functions';

const HIDE_AGENCY = true;

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_MEMBERSHIPS': {
      return {
        ...state,
        memberships: action.payload.map((m) => ({ ...m, key: uuid() })),
      };
    }
    case 'REMOVE_MEMBERSHIP': {
      return {
        ...state,
        memberships: state.memberships.filter(({ key }) => key !== action.payload.key),
      };
    }
    case 'ADD_MEMBERSHIP': {
      return {
        ...state,
        memberships: [...state.memberships, { key: uuid(), client: {}, role: {} }],
      };
    }
    case 'UPDATE_CLIENT': {
      return {
        ...state,
        memberships: state.memberships.map((m) =>
          m.key === action.payload.key ? { ...m, client: action.payload.client } : m
        ),
      };
    }
    case 'UPDATE_ROLE': {
      return {
        ...state,
        memberships: state.memberships.map((m) =>
          m.key === action.payload.key ? { ...m, role: action.payload.role } : m
        ),
      };
    }
    case 'UPDATE_SITE_ROLE': {
      return {
        ...state,
        siteRole: action.payload,
      };
    }
    default:
      return state;
  }
};

const defaultValues = {
  username: '',
  email: '',
  givenName: '',
  familyName: '',
  address: '',
  team: '',
  memberships: [],
  siteRole: '',
};

function AddUser({
  history,
  clients,
  profile,
  fetchClientsStatus,
  agencies,
  fetchAgenciesStatus,
  registerStatus,
  registerError,
  fetchClients,
  fetchAgencies,
  register,
  enqueueNotification,
}) {
  const [state, dispatch] = useReducer(reducer, {
    memberships: [{ key: uuid(), client: {}, role: {} }],
  });

  const { handleSubmit, errors, control, setValue, register: registerInput, reset, watch } = useForm({ defaultValues });
  const watchEmail = watch('email');

  /* eslint-disable no-param-reassign */
  const onSubmit = (data) => {
    const { type } = data;
    if (type === 'AGENCY') {
      data.memberships = [];
    }
    register(data);
  };
  /* eslint-enable no-param-reassign */

  const [agency, setAgency] = useState(null);
  const [userType, setUserType] = useState('CLIENT');

  const registerInputs = useCallback(() => {
    registerInput({ name: 'type', type: 'custom' }, { required: true });
    setValue('type', userType);
    registerInput(
      { name: 'memberships', type: 'custom' },
      {
        validate: (memberships) =>
          watch('type') === 'AGENCY' ||
          (watch('type') === 'CLIENT' &&
            !!memberships &&
            memberships.length > 0 &&
            memberships.every((m) => m.client && m.client.tenant && m.role && m.role.name)) ||
          'There are error(s) in the client membership',
      }
    );
    registerInput(
      { name: 'agency', type: 'custom' },
      {
        validate: (value) =>
          watch('type') === 'CLIENT' || (watch('type') === 'AGENCY' && !!value) || 'Agency membership is not specified',
      }
    );
    registerInput(
      { name: 'siteRole', type: 'custom' },
      {
        validate: (value) =>
          ['', null, undefined, CONSTANTS.ROLE.CLIENT_USER].includes(value?.name) ||
          ([CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES].includes(
            value?.name
          ) &&
            watch('email').match(/@pinpoint\.ai$/)) ||
          'User must have a Pinpoint email address',
      }
    );
  }, [watch, registerInput, setValue, userType]);

  const checkClientEmailWhitelist = useCallback(
    (email) => {
      const whitelist = state.memberships.map(({ client }) => client.emailWhitelist);
      const whitelistCs = whitelist.join(',');
      // wildcard check left for backwards compatibility
      if (whitelistCs.includes('*')) return true;
      const whitelistCombined = whitelistCs.split(',').filter((elem) => ![null, undefined, ''].includes(elem));
      const emailDomain = email.split('@')[1];
      return (
        whitelistCombined.some((allowedDomain) => allowedDomain === emailDomain) ||
        `Email address is not whitelisted. Allowed domains: ${whitelistCombined.join(',')}`
      );
    },
    [state.memberships]
  );

  useEffect(() => {
    setValue('memberships', state.memberships, { shouldValidate: false });
  }, [state.memberships, setValue]);

  useEffect(() => {
    setValue('siteRole', state.siteRole, { shouldValidate: true });
  }, [state.siteRole, setValue, watchEmail]);

  useEffect(() => {
    fetchClients();
    fetchAgencies();
    registerInputs();
    // NOTE: needs to run once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (registerStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(registerError);
    }
    if (registerStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('User successfully registered', { autoClose: true });
      setTimeout(() => {
        reset(defaultValues);
        setAgency(null);
        registerInputs();
      }, 2000);
    }
  }, [registerStatus, registerError, enqueueNotification, registerInputs, reset]);

  return (
    <Container>
      <Row p={{ t: '1.25rem' }}>
        <Col d="flex" size="6" gutterWidth="1.25rem">
          <Div>
            <Button
              buttonStyle="primary"
              onClick={() => {
                history.push('/users');
              }}
              data-tip="go back"
            >
              <Icon color="white" name="LongLeft" size="1rem" m={{ r: '.5em' }} />
              BACK
            </Button>
          </Div>
        </Col>
      </Row>
      <Div bg="white" m={{ y: '1rem' }} p={{ x: '1.5rem', t: '2.25rem', b: '1rem' }} rounded="md">
        <Container>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12', md: '2' }}>
                <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                  <Text textSize="subtitle">Username</Text>
                </Div>
              </Col>
              <Col d="flex" align="center" size={{ xs: '12', md: '10' }}>
                <Controller
                  name="username"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'Username is required',
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="username" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12', md: '2' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="subtitle">Email</Text>
                </Div>
              </Col>
              <Col d="flex" align="center" size={{ xs: '12', md: '10' }}>
                <Controller
                  name="email"
                  as={Input}
                  control={control}
                  disabled={state.memberships.length > 0 && !(Object.keys(state.memberships[0].client).length > 0)}
                  rules={{
                    validate: checkClientEmailWhitelist,
                    required: 'Email is required',
                    pattern: {
                      value: /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
                      message: 'The email is invalid',
                    },
                  }}
                  placeholder={
                    state.memberships.length > 0 && !(Object.keys(state.memberships[0].client).length > 0)
                      ? 'Please select a client'
                      : null
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="email" />
                </Text>
              </Col>
            </Row>

            <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN]}>
              <Row p={{ b: '1rem' }}>
                <Col size={{ xs: '12', md: '2' }} gutterWidth="1.25rem">
                  <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                    <Text textSize="subtitle">Site Role</Text>
                  </Div>
                </Col>
                <Col d="flex" align="center" size={{ xs: '12', md: '10' }} gutterWidth="1.25rem">
                  <SiteRolesDropdown
                    w="100%"
                    defaultValue="Please select a role"
                    onSelectRole={(roleName) => {
                      const nextRole = { name: roleName };
                      dispatch({
                        type: 'UPDATE_SITE_ROLE',
                        payload: nextRole,
                      });
                    }}
                    currentRole={profile.siteRole || profile.role}
                  />
                </Col>
              </Row>
              <Row>
                <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                  <Text textColor="danger800" textSize="caption">
                    <ErrorMessage errors={errors} name="siteRole" />
                  </Text>
                </Col>
              </Row>
            </Rbac>

            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12', md: '2' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="subtitle">First Name</Text>
                </Div>
              </Col>
              <Col d="flex" align="center" size={{ xs: '12', md: '10' }}>
                <Controller
                  name="givenName"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'First name is required',
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="givenName" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12', md: '2' }}>
                <Div justify="flex-end" d="flex" align="center" minH="2.5rem">
                  <Text textSize="subtitle">Last Name</Text>
                </Div>
              </Col>
              <Col d="flex" align="center" size={{ xs: '12', md: '10' }}>
                <Controller
                  name="familyName"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'Last name is required',
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="familyName" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12', md: '2' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="subtitle">Team</Text>
                </Div>
              </Col>
              <Col d="flex" align="center" size={{ xs: '12', md: '10' }}>
                <Controller name="team" as={Input} control={control} rules={{}} />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="team" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }}>
              <Col size={{ xs: '12', md: '2' }}>
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="subtitle">Address</Text>
                </Div>
              </Col>
              <Col d="flex" align="center" size={{ xs: '12', md: '10' }}>
                <Controller
                  name="address"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'Address is required',
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="address" />
                </Text>
              </Col>
            </Row>
            {HIDE_AGENCY ? null : (
              <Row p={{ y: '1rem' }}>
                <Col size={{ xs: '12', md: '4' }}>
                  <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                    <Text textSize="subtitle">Type</Text>
                  </Div>
                </Col>
                <Col size={{ xs: '12', md: '4' }}>
                  <Label
                    tag="div"
                    align="center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newType = userType === 'CLIENT' ? 'AGENCY' : 'CLIENT';
                      setUserType(newType);
                      setValue('type', newType);
                    }}
                  >
                    <Text>Agency User&nbsp;&nbsp;&nbsp;</Text>
                    <Switch
                      checked={userType === 'CLIENT'}
                      activeColor="brand500"
                      inactiveColor="brand500"
                      activeShadow="0"
                    />
                    <Text>Customer</Text>
                  </Label>
                </Col>
              </Row>
            )}
            {userType === 'CLIENT' ? (
              <>
                <Row p={{ b: '1rem' }}>
                  <Col size={{ xs: '12', md: '4' }}>
                    <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                      <Text textSize="subtitle">Client</Text>
                    </Div>
                  </Col>
                  {/*
                        <Col size={{ xs: '12', md: '4' }}>
                          <ClientsSelection
                            w="100%"
                            loading={fetchClientsStatus === CONSTANTS.API.STARTED}
                            clients={clients}
                            tenant={tenant}
                            defaultText="Please select a client"
                            onSelectClient={(client) => {
                              setValue('client', client, { shouldValidate: true });
                              setTenant(client.tenant);
                            }}
                          />
                        </Col>
                        <Col size={{ xs: '12', md: '4' }}>
                          <RolesDropdown
                            w="100%"
                            defaultValue={roleName || 'Please select a role'}
                            onSelectRole={(name) => {
                              const role = { name };
                              setValue('role', role, { shouldValidate: true });
                              setRoleName(name);
                            }}
                            currentRole={profile.role}
                          />
                        </Col>
                        */}
                  <Col size={{ xs: '12', md: '8' }}>
                    {state.memberships.map(({ client, role, key }) => (
                      <Row d="flex" align="center" p={{ b: '1rem' }}>
                        <Col size={{ xs: '12', md: '6' }}>
                          <ClientsSelection
                            w="100%"
                            loading={fetchClientsStatus === CONSTANTS.API.STARTED}
                            clients={clients}
                            tenant={client && client.tenant}
                            defaultText="Please select a client"
                            onSelectClient={(nextClient) => {
                              dispatch({
                                type: 'UPDATE_CLIENT',
                                payload: {
                                  client: nextClient,
                                  key,
                                },
                              });
                            }}
                          />
                        </Col>
                        <Col size={{ xs: '12', md: '5' }}>
                          <RolesDropdown
                            w="100%"
                            defaultValue={CONSTANTS.ROLE_RENAMED[role.name] || role.name || 'Please select a role'}
                            onSelectRole={(roleName) => {
                              const nextRole = { name: roleName };
                              dispatch({
                                type: 'UPDATE_ROLE',
                                payload: {
                                  role: nextRole,
                                  key,
                                },
                              });
                            }}
                            currentRole={profile.role}
                          />
                        </Col>
                        <Col size={{ xs: '12', md: '1' }}>
                          <Button
                            buttonStyle="primary"
                            disabled={state.memberships.length === 1}
                            onClick={() => {
                              dispatch({
                                type: 'REMOVE_MEMBERSHIP',
                                payload: {
                                  key,
                                },
                              });
                            }}
                          >
                            <Icon color="white" name="Delete" size="1rem" />
                          </Button>
                        </Col>
                      </Row>
                    ))}
                  </Col>
                </Row>
                <Row p={{ b: '1rem' }}>
                  <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                    <Text textColor="danger800" textSize="caption">
                      <ErrorMessage errors={errors} name="memberships" />
                    </Text>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <Row p={{ b: '1rem' }}>
                  <Col size={{ xs: '12', md: '4' }}>
                    <Div d="flex" align="center" minH="2.5rem">
                      <Text textSize="subtitle">Agency</Text>
                    </Div>
                  </Col>
                  <Col size={{ xs: '12', md: '8' }}>
                    <AgencyDropdown
                      w="100%"
                      loading={fetchAgenciesStatus === CONSTANTS.API.STARTED}
                      agencies={agencies}
                      agency={agency}
                      defaultText="Please select a agency"
                      onSelectAgency={(selectedAgency) => {
                        setValue('agency', selectedAgency, { shouldValidate: true });
                        setAgency(selectedAgency);
                      }}
                    />
                  </Col>
                </Row>
                <Row p={{ b: '1rem' }}>
                  <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                    <Text textColor="danger800" textSize="caption">
                      <ErrorMessage errors={errors} name="agency" />
                    </Text>
                  </Col>
                </Row>
              </>
            )}
            <Row>
              <Col offset="4" size={{ xs: '12', md: '2' }} gutterWidth="1.25rem">
                <Button
                  buttonStyle="primary"
                  onClick={() => {
                    dispatch({
                      type: 'ADD_MEMBERSHIP',
                    });
                  }}
                >
                  <Icon name="Plus" color="white" m={{ r: '0.5rem' }} size="1rem" />
                  ADD MEMBERSHIP
                </Button>
              </Col>
            </Row>
            <Row d="flex" justify="flex-end" p={{ b: '1rem' }}>
              <Col>
                <Button buttonStyle="primary" m={{ r: '0rem' }} onClick={handleSubmit(onSubmit)}>
                  <Text color="gray400">SAVE</Text>
                  {registerStatus === CONSTANTS.API.STARTED ? (
                    <Icon color="gray400" name="Loading" m={{ x: '0.5rem' }} />
                  ) : null}
                </Button>
              </Col>
            </Row>
          </form>
        </Container>
      </Div>
    </Container>
  );
}

const mapStateToProps = ({ app, users }) => ({
  ...app,
  ...users,
});

const mapDispatchToProps = (dispatch) => ({
  fetchClients: () => dispatch(appActions.fetchClients()),
  fetchAgencies: () => dispatch(usersActions.fetchAgencies()),
  register: (payload) => dispatch(appActions.register(payload)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

AddUser.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fetchClientsStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  agencies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fetchAgenciesStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  registerStatus: PropTypes.string.isRequired,
  registerError: PropTypes.string.isRequired,
  fetchClients: PropTypes.func.isRequired,
  fetchAgencies: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  profile: PropTypes.shape({
    role: PropTypes.string,
    siteRole: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(AddUser));
